import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { OnboardingChoice } from "@/components/onboarding/onboarding-choice";
import { OnboardingPasswordForm } from "@/components/onboarding/onboarding-password-form";
import { InviteInvalid } from "@/components/onboarding/invite-invalid";
import { inviteByTokenOptions } from "@/api/invites/query-options";
import { useSetPassword, useStartMicrosoft } from "@/api/invites/mutations";

const searchSchema = z.object({ error: z.enum(["email-mismatch"]).optional() });

export const Route = createFileRoute("/onboarding/$token")({
  validateSearch: searchSchema,
  component: OnboardingTokenPage,
});

export function OnboardingTokenPage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const { error } = Route.useSearch();
  const navigate = useNavigate({ from: "/onboarding/$token" });
  const invite = useQuery(inviteByTokenOptions(token));
  const setPasswordMut = useSetPassword();
  const startMsMut = useStartMicrosoft();
  const [mode, setMode] = useState<"choice" | "password">("choice");

  if (invite.isError) return <InviteInvalid />;
  if (!invite.data) return null;

  async function startMicrosoft() {
    const result = await startMsMut.mutateAsync({ token });
    window.location.assign(result.authorizeUrl);
  }

  if (mode === "password") {
    return (
      <div className="min-h-dvh bg-surface-2">
        <OnboardingPasswordForm
          isPending={setPasswordMut.isPending}
          onSubmit={async (password) => {
            await setPasswordMut.mutateAsync({ token, body: { password } });
            navigate({ to: "/onboarding/mfa" });
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-surface-2">
      {error === "email-mismatch" ? (
        <div className="mx-auto mt-6 max-w-md rounded-3 border border-line bg-surface-1 p-3 text-[12.5px] text-vroom-red">
          {t("onboarding.token-route.email-mismatch")}
        </div>
      ) : null}
      <OnboardingChoice
        name={invite.data.name}
        hasPendingMicrosoftHint={invite.data.hasPendingMicrosoftHint}
        onMicrosoft={startMicrosoft}
        onPasswordChosen={() => setMode("password")}
      />
    </div>
  );
}
